import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Loader2, Pencil, Trash2, ClipboardList, Boxes, MapPin, Activity, Save, Download, Upload, FileSpreadsheet,
} from "lucide-react";
import api, { formatApiErrorDetail } from "./api";
import { useAuth } from "./AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
const STATUS = ["DONE", "PENDING", "CANCEL", "NA"];
const CITIES = ["Delhi", "Gurugram", "Noida", "Faridabad", "Ghaziabad"];

const statusColor = (s) => ({ DONE: "#22c55e", PENDING: "#f59e0b", CANCEL: "#ef4444", NA: "#64748b" }[s] || "#64748b");

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  date: today(), city: "", site_id: "", gis_rsu_code: "",
  installation_status: "PENDING", installation_node: "",
  online_at: "NA", service_at: "NA", at_node: "",
  lc_lc: { m2: 0, m5: 0, m10: 0, m15_20: 0 },
  sc_apc: { m2: 0, m5: 0, m10: 0 },
  flexible_mtr: 0, remarks: "",
});

const Num = ({ label, value, onChange, testid }) => (
  <div>
    <label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>{label}</label>
    <input type="number" min="0" className="field !py-2 !px-2 text-center" value={value}
      onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))} data-testid={testid} />
  </div>
);

function EntryForm({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial?.id ? { ...emptyForm(), ...initial } : emptyForm());
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMat = (grp, k, v) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/field/activities/${initial.id}`, form);
      else await api.post("/field/activities", form);
      toast.success(isEdit ? "Entry updated" : "Entry logged");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const selCls = "field appearance-none cursor-pointer";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0C1322] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="entry-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-white">{isEdit ? "Edit Activity" : "New Daily Activity"}</DialogTitle>
          <DialogDescription style={{ color: "#64748B" }}>Record the day's site activity status and material consumed.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="entry-form">
          {/* Activity */}
          <div>
            <div className="mono-label mb-3" style={{ color: "#00F0FF" }}>Activity</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div><label className="mono-label block mb-1.5" style={{ color: "#94A3B8" }}>Date *</label>
                <input type="date" className="field" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="entry-date" required /></div>
              <div><label className="mono-label block mb-1.5" style={{ color: "#94A3B8" }}>City</label>
                <input className="field" list="cities" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="entry-city" placeholder="Delhi" />
                <datalist id="cities">{CITIES.map((c) => <option key={c} value={c} />)}</datalist></div>
              <div><label className="mono-label block mb-1.5" style={{ color: "#94A3B8" }}>Site ID</label>
                <input className="field" value={form.site_id} onChange={(e) => set("site_id", e.target.value)} data-testid="entry-site" placeholder="R6D-L1,L2,L3" /></div>
              <div><label className="mono-label block mb-1.5" style={{ color: "#94A3B8" }}>GIS/RSU Code</label>
                <input className="field" value={form.gis_rsu_code} onChange={(e) => set("gis_rsu_code", e.target.value)} data-testid="entry-gis" placeholder="e.g. GIS-4521" /></div>
            </div>
          </div>

          {/* Status */}
          <div>
            <div className="mono-label mb-3" style={{ color: "#00F0FF" }}>Status</div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div><label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>Installation</label>
                <select className={selCls} value={form.installation_status} onChange={(e) => set("installation_status", e.target.value)} data-testid="entry-install-status">{STATUS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div><label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>Install Node</label>
                <input className="field !py-2 !px-2" value={form.installation_node} onChange={(e) => set("installation_node", e.target.value)} /></div>
              <div><label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>Online AT</label>
                <select className={selCls} value={form.online_at} onChange={(e) => set("online_at", e.target.value)}>{STATUS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div><label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>Service AT</label>
                <select className={selCls} value={form.service_at} onChange={(e) => set("service_at", e.target.value)}>{STATUS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div><label className="mono-label block mb-1.5 text-[9px]" style={{ color: "#64748B" }}>AT Node</label>
                <input className="field !py-2 !px-2" value={form.at_node} onChange={(e) => set("at_node", e.target.value)} /></div>
            </div>
          </div>

          {/* Materials */}
          <div>
            <div className="mono-label mb-3" style={{ color: "#00F0FF" }}>Material Consumed</div>
            <div className="bg-[#060A14] border border-white/10 p-4 mb-3">
              <div className="text-xs text-slate-300 mb-2 font-medium">LC-LC Patch Cords</div>
              <div className="grid grid-cols-4 gap-2">
                <Num label="2 MTR" value={form.lc_lc.m2} onChange={(v) => setMat("lc_lc", "m2", v)} testid="lclc-m2" />
                <Num label="5 MTR" value={form.lc_lc.m5} onChange={(v) => setMat("lc_lc", "m5", v)} testid="lclc-m5" />
                <Num label="10 MTR" value={form.lc_lc.m10} onChange={(v) => setMat("lc_lc", "m10", v)} testid="lclc-m10" />
                <Num label="15/20 MTR" value={form.lc_lc.m15_20} onChange={(v) => setMat("lc_lc", "m15_20", v)} testid="lclc-m1520" />
              </div>
            </div>
            <div className="bg-[#060A14] border border-white/10 p-4 mb-3">
              <div className="text-xs text-slate-300 mb-2 font-medium">SC-APC Patch Cords</div>
              <div className="grid grid-cols-3 gap-2">
                <Num label="2 MTR" value={form.sc_apc.m2} onChange={(v) => setMat("sc_apc", "m2", v)} testid="scapc-m2" />
                <Num label="5 MTR" value={form.sc_apc.m5} onChange={(v) => setMat("sc_apc", "m5", v)} testid="scapc-m5" />
                <Num label="10 MTR" value={form.sc_apc.m10} onChange={(v) => setMat("sc_apc", "m10", v)} testid="scapc-m10" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Num label="Flexible (MTR)" value={form.flexible_mtr} onChange={(v) => set("flexible_mtr", v)} testid="flexible-mtr" />
            </div>
          </div>

          <div>
            <label className="mono-label block mb-1.5" style={{ color: "#94A3B8" }}>Remarks</label>
            <textarea rows={2} className="field resize-none" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="entry-remarks" placeholder="e.g. 48 PC PUT IN RACK" />
          </div>

          <button type="submit" disabled={saving} data-testid="save-entry"
            className="group w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3.5 mono-label transition-colors duration-300 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> {isEdit ? "Update Entry" : "Save Entry"}</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SummaryCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#0C1322] border border-white/10 p-5" data-testid={testid}>
    <div className="flex items-center justify-between"><Icon size={18} style={{ color: "#00F0FF" }} /><span className="mono-label" style={{ color: "#64748B" }}>{label}</span></div>
    <div className="mt-4 font-display font-black text-3xl text-white">{value}</div>
  </div>
);

export default function FieldTracker() {
  const { user } = useAuth();
  const now = new Date();
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [statusFilter, setStatusFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (statusFilter) params.status = statusFilter;
      if (siteFilter) params.site_id = siteFilter;
      const [a, s] = await Promise.all([
        api.get("/field/activities", { params }),
        api.get("/field/summary", { params: { month, year } }),
      ]);
      setList(a.data);
      setSummary(s.data);
    } catch {
      toast.error("Failed to load field activities");
    } finally {
      setLoading(false);
    }
  }, [month, year, statusFilter, siteFilter]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/field/activities/${deleteTarget.id}`);
      toast.success("Activity removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/field/export", { params: { month, year }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `A3NETWORKS_Field_Tracker_${year}_${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Choose an .xlsx file"); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post("/field/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      toast.success(`${data.imported} row(s) imported`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const mt = summary?.material_totals;

  return (
    <div data-testid="field-tracker-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <span className="mono-label" style={{ color: "#00F0FF" }}>Daily Site Activity &amp; Material Tracker</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight flex items-center gap-3">
            <ClipboardList size={30} style={{ color: "#00F0FF" }} /> Field Tracker
          </h1>
          <p className="text-sm mt-1" style={{ color: "#64748B" }}>
            {user?.role === "employee" ? "Log your daily site activities and material usage." : "All field engineers' daily activities and material consumption."}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => { setUploadFile(null); setUploadResult(null); setUploadOpen(true); }} data-testid="upload-button"
            className="group inline-flex items-center gap-2 border border-white/15 text-white hover:border-cyan px-5 py-3 mono-label transition-colors duration-300">
            <Upload size={16} /> Upload
          </button>
          <button onClick={exportExcel} data-testid="export-excel-button"
            className="group inline-flex items-center gap-2 border border-white/15 text-white hover:border-cyan px-5 py-3 mono-label transition-colors duration-300">
            <Download size={16} /> Export to Excel
          </button>
          <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-entry-button"
            className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors duration-300">
            <Plus size={16} /> New Entry
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select className="field appearance-none cursor-pointer !w-auto" value={month} onChange={(e) => setMonth(parseInt(e.target.value))} data-testid="filter-month">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" className="field !w-28" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} data-testid="filter-year" />
        <select className="field appearance-none cursor-pointer !w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="">All Status</option>{STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input className="field !w-48" placeholder="Filter by Site ID" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} data-testid="filter-site" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <SummaryCard icon={Activity} label="Activities" value={summary?.total_activities ?? "—"} testid="sum-activities" />
        <SummaryCard icon={MapPin} label="Unique Sites" value={summary?.unique_sites ?? "—"} testid="sum-sites" />
        <SummaryCard icon={Boxes} label="Total Material" value={mt ? mt.grand_total : "—"} testid="sum-material" />
        <div className="bg-[#0C1322] border border-white/10 p-5" data-testid="sum-status">
          <span className="mono-label" style={{ color: "#64748B" }}>Status</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {summary && Object.entries(summary.status_counts).filter(([, v]) => v).map(([k, v]) => (
              <span key={k} className="mono-label px-2 py-1 border" style={{ color: statusColor(k), borderColor: statusColor(k) + "55" }}>{k} {v}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Material breakdown */}
      {mt && (
        <div className="bg-[#0C1322] border border-white/10 p-5 mb-6" data-testid="material-breakdown">
          <div className="mono-label mb-3" style={{ color: "#94A3B8" }}>Total Material Consumed — {MONTHS[month - 1]} {year}</div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-300">
            <span>LC-LC · 2M <b className="text-white">{mt.lc_lc.m2}</b></span>
            <span>5M <b className="text-white">{mt.lc_lc.m5}</b></span>
            <span>10M <b className="text-white">{mt.lc_lc.m10}</b></span>
            <span>15/20M <b className="text-white">{mt.lc_lc.m15_20}</b></span>
            <span className="text-slate-500">|</span>
            <span>SC-APC · 2M <b className="text-white">{mt.sc_apc.m2}</b></span>
            <span>5M <b className="text-white">{mt.sc_apc.m5}</b></span>
            <span>10M <b className="text-white">{mt.sc_apc.m10}</b></span>
            <span className="text-slate-500">|</span>
            <span>Flexible <b className="text-white">{mt.flexible_mtr}</b> MTR</span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#0C1322] border border-white/10 overflow-x-auto">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "#00F0FF" }} /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-sm" style={{ color: "#64748B" }} data-testid="field-empty">No activities logged for this period.</div>
        ) : (
          <table className="w-full text-sm whitespace-nowrap" data-testid="field-table">
            <thead>
              <tr className="border-b border-white/10 text-left" style={{ color: "#64748B" }}>
                <th className="p-3 mono-label font-normal">Date</th>
                <th className="p-3 mono-label font-normal">City</th>
                {user?.role !== "employee" && <th className="p-3 mono-label font-normal">Engineer</th>}
                <th className="p-3 mono-label font-normal">Site ID</th>
                <th className="p-3 mono-label font-normal">GIS/RSU Code</th>
                <th className="p-3 mono-label font-normal">Install</th>
                <th className="p-3 mono-label font-normal">Online AT</th>
                <th className="p-3 mono-label font-normal">Service AT</th>
                <th className="p-3 mono-label font-normal">Remarks</th>
                <th className="p-3 mono-label font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-[#131C31] transition-colors" data-testid={`field-row-${a.id}`}>
                  <td className="p-3 text-white">{a.date}</td>
                  <td className="p-3 text-slate-300">{a.city || "—"}</td>
                  {user?.role !== "employee" && <td className="p-3 text-slate-400">{a.engineer_name || "—"}</td>}
                  <td className="p-3 text-slate-300">{a.site_id || "—"}</td>
                  <td className="p-3 text-slate-300">{a.gis_rsu_code || "—"}</td>
                  <td className="p-3"><span className="mono-label px-2 py-0.5 border" style={{ color: statusColor(a.installation_status), borderColor: statusColor(a.installation_status) + "55" }}>{a.installation_status}</span></td>
                  <td className="p-3"><span style={{ color: statusColor(a.online_at) }}>{a.online_at}</span></td>
                  <td className="p-3"><span style={{ color: statusColor(a.service_at) }}>{a.service_at}</span></td>
                  <td className="p-3 text-slate-400 max-w-[200px] truncate" title={a.remarks}>{a.remarks || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`edit-field-${a.id}`} className="p-2 text-slate-400 hover:text-cyan transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => setDeleteTarget(a)} data-testid={`delete-field-${a.id}`} className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EntryForm open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} onSaved={load} />

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) { setUploadOpen(false); setUploadResult(null); setUploadFile(null); } }}>
        <DialogContent className="bg-[#0C1322] border-white/10 text-white" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">Upload Field Tracker</DialogTitle>
            <DialogDescription style={{ color: "#94A3B8" }}>
              Import activities from an existing .xlsx tracker. Both the old and new column layouts are supported — the system auto-detects columns.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitUpload} className="space-y-4" data-testid="upload-form">
            <label className="block border-2 border-dashed border-white/15 hover:border-cyan p-8 text-center cursor-pointer transition-colors" data-testid="upload-dropzone">
              <FileSpreadsheet size={32} className="mx-auto mb-2" style={{ color: "#00F0FF" }} />
              <div className="text-sm text-white">{uploadFile ? uploadFile.name : "Click to choose an .xlsx file"}</div>
              <div className="mono-label mt-1" style={{ color: "#64748B" }}>Excel (.xlsx / .xlsm) only</div>
              <input type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="upload-file-input"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </label>
            {uploadResult && (
              <div className="bg-[#060A14] border border-white/10 p-4 text-sm space-y-1" data-testid="upload-result">
                <div className="text-white">Imported: <b style={{ color: "#00F0FF" }}>{uploadResult.imported}</b></div>
                <div className="text-slate-400">Skipped (blank/total rows): {uploadResult.skipped}</div>
                <div className="mono-label mt-2" style={{ color: "#64748B" }}>
                  Detected: {uploadResult.detected_columns?.join(", ")}
                </div>
              </div>
            )}
            <button type="submit" disabled={uploading || !uploadFile} data-testid="upload-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3.5 mono-label transition-colors disabled:opacity-60">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} /> Import Now</>}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0C1322] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove activity?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#94A3B8" }}>
              This will permanently delete the {deleteTarget?.date} entry for site {deleteTarget?.site_id || "—"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-field" className="bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
